import { reactive, ref } from 'vue'
import { githubApi } from '@/api/github'
import { withAsyncState } from '@/lib/asyncState'
import type { GithubRepositorySettings } from '@/types/models'

// Singleton: shared between the Repositories list and the per-repo settings
// page so toggling a flag in one place is reflected in the other.
const settings = reactive<Record<string, GithubRepositorySettings>>({})
const loading = ref(false)
const error = ref<string | null>(null)
let loadedInstallationId: string | null = null

export function useGithubRepositorySettings() {
  async function fetchFor(installationId: string, force = false): Promise<void> {
    if (loadedInstallationId === installationId && !force) return

    await withAsyncState(
      error,
      'Failed to load repository settings.',
      async () => {
        const list = await githubApi.repositorySettings(installationId)
        for (const item of list) settings[item.repo_full_name] = item
        loadedInstallationId = installationId
      },
      loading,
    )
  }

  function get(repoFullName: string): GithubRepositorySettings {
    return settings[repoFullName] ?? {
      id: '',
      repo_full_name: repoFullName,
      stack: null,
      auto_review_enabled: false,
      wait_for_other_checks_enabled: false,
      auto_scan_enabled: false,
      code_graph_enabled: false,
      auto_fix_comments_enabled: false,
      auto_fix_max_attempts: 3,
      test_evidence_enabled: false,
      independent_verification_enabled: false,
      visual_evidence_enabled: false,
      publish_build_evidence_enabled: false,
      self_check_before_pr_enabled: false,
      review_profile: 1,
      review_type_default: 1,
      severity_threshold: 2,
      model: 1,
      finding_reply_model: null,
      repo_rule_model: 1,
      auto_fix_model: null,
      incremental_by_default: true,
      draft_handling: 1,
      slack_webhook_configured: false,
      review_rules: [],
      path_filters: [],
    }
  }

  async function update(
    installationId: string,
    repoFullName: string,
    attrs: Partial<Omit<GithubRepositorySettings, 'repo_full_name'>>,
  ): Promise<void> {
    await withAsyncState(error, 'Failed to update repository settings.', async () => {
      settings[repoFullName] = await githubApi.updateRepositorySettings(installationId, {
        repo_full_name: repoFullName,
        ...attrs,
      })
    })
  }

  // Called on logout so the next session doesn't briefly show the previous
  // user's repository settings before its own fetch resolves.
  function reset(): void {
    for (const key of Object.keys(settings)) delete settings[key]
    loading.value = false
    error.value = null
    loadedInstallationId = null
  }

  return { settings, loading, error, fetchFor, get, update, reset }
}
